import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { StatusBadge } from "@/components/StatusBadge";
import { FieldRow } from "@/components/record/FieldRow";
import { RecordPage } from "@/components/record/RecordPage";
import type { RecordField } from "@/components/record/fields";
import type { DataTableColumn } from "@/components/table/columns";
import type { InlineEditCommitArgs, InlineEditConflictArgs } from "@/components/table/use-inline-edits";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { TenantsIcon } from "@/components/ui/icons";
import { ApiError, apiFetch } from "@/lib/api";
import type { NotificationPreferences, Role, UserResource } from "@/lib/api-types";
import { useIsOwner, useIsReadOnly } from "@/lib/current-actor";
import { ROLE_LABELS } from "@/lib/enum-labels";
import { useEnums } from "@/lib/enums";
import { formatDateCell } from "@/lib/format";
import {
  conflictMessage,
  freshestKnownValue,
  type EditCommitOutcome,
  type EditCommitPayload,
} from "@/lib/inline-edit";
import { showToast } from "@/lib/toast-queue";

/**
 * The value half of `PATCH /users/{id}`'s body a commit produces — the
 * identical shape `UsersList.tsx`'s own `patchBodyFor` builds, because this
 * is the same route and the same request, just fired from a field row
 * instead of a table cell (spec judgement call 6).
 */
function patchBodyFor(fieldKey: string, payload: EditCommitPayload): Record<string, unknown> {
  if (payload.kind !== "value") {
    throw new Error(`unexpected transition-kind payload for field "${fieldKey}"`);
  }
  return { [fieldKey]: payload.value };
}

/** Same reasoning as `UsersList.tsx`'s own `labelForConflictValue`: only
 * `role` is a fixed-option field with raw values nobody has seen written
 * out; `display_name` is free text already in plain language. */
function labelForConflictValue(field: string, rawValue: string): string {
  return field === "role" ? (ROLE_LABELS[rawValue as Role] ?? rawValue) : rawValue;
}

const NOTIFICATION_YES_NO_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

function formatNotificationYesNo(value: boolean): string {
  return value ? "Yes" : "No";
}

interface NotificationToggleDef {
  key: keyof NotificationPreferences;
  label: string;
  description: string;
}

/**
 * Story 15.11's own four toggles, wired to the real `NotificationSubscription`
 * columns (`app/repo/models/notification_subscription.py`) through the
 * already-existing, already-tested `GET`/`PATCH /users/{user_id}/notifications`
 * (`app/api/users.py`). Every description below is verified against the real
 * dispatch logic, not assumed from the field name (spec judgement call 3):
 *
 * - `weekly_digest` — despite the field/column name, `app/domain/alerting/
 *   dispatch.py`'s own module docstring says this plainly: "its name is a
 *   holdover; R-A7 caps it at one *per day*, not one per week." The label
 *   below says "Daily", never "Weekly" (this story's own Intent correction)
 *   — the column name itself is untouched (out of scope: a rename is a
 *   separate, larger decision).
 * - `weekly_heartbeat` — genuinely weekly: `app/domain/alerting/heartbeat.py`
 *   sends it unconditionally every Monday (a real internal Monday gate, plus
 *   a durable before-the-send dedup so a second same-day run never
 *   double-sends), through the transactional channel alone — never bundled
 *   into the daily digest above.
 * - `immediate_alerts` — gates `dispatch.py`'s own `IMMEDIATE_SEND_ALLOWLIST`:
 *   `backup-failed`, `backup-missing`, `email-delivery-failure`, and
 *   `emergency-work-order` — real, time-sensitive event types, sent right
 *   away rather than waiting for the next daily digest.
 * - `system_health_alerts` — a *further* restriction on top of
 *   `immediate_alerts`, scoped to exactly `dispatch.py`'s own
 *   `SYSTEM_HEALTH_RULE_KEYS` (backup failed/missing, email delivery
 *   failure) — never a property-related alert type.
 */
const NOTIFICATION_TOGGLES: NotificationToggleDef[] = [
  {
    key: "weekly_digest",
    label: "Daily alert digest",
    description: "One email a day bundling any open alerts you weren't already sent right away.",
  },
  {
    key: "weekly_heartbeat",
    label: "Weekly heartbeat",
    description: "A confirmation email every Monday, even when there's nothing new to report.",
  },
  {
    key: "immediate_alerts",
    label: "Immediate alerts",
    description:
      "Time-sensitive alerts — like a backup failure or an emergency work order — sent right away instead of waiting for the daily digest.",
  },
  {
    key: "system_health_alerts",
    label: "System health alerts",
    description: "Notifications about the app itself (backups, email delivery), not about your properties.",
  },
];

/**
 * One toggle row (spec judgement call 1): the same click-to-reveal-a-Yes/No-
 * select shape `TenantRecord.tsx`'s own `yesNoEdit` (`id_verified`) already
 * uses, deliberately **not** wired through `RecordPage`/`InlineEditCell`'s
 * shared inline-edit machinery, for a real reason (the exception judgement
 * call 1 itself anticipates): that machinery's own failure state keeps
 * showing the *attempted* value with a warning dot until a retry succeeds
 * (`InlineEditCell.tsx`'s own `unsaved`/`renderedValue` — correct for a text
 * field, where "keep what I typed so I can retry it" is the point). VC-15.11
 * -04 asks for the opposite on a toggle: a failed save **reverts** to the
 * last real value the server confirmed, with the error shown separately —
 * mirroring `PropertyRecord.tsx`'s own precedent (`DispositionClosingDocuments
 * Field`) of a bespoke editor, built directly, once a field's real save
 * semantics don't fit the shared cell's one shape. `value` is always the
 * query's own cached server value — never a locally-held optimistic guess —
 * so a failed `onSave` leaves this component with nothing to revert at all:
 * the prop never changed in the first place.
 */
function NotificationToggleRow({
  toggle,
  value,
  editable,
  onSave,
}: {
  toggle: NotificationToggleDef;
  value: boolean;
  editable: boolean;
  onSave: (key: keyof NotificationPreferences, next: boolean) => Promise<EditCommitOutcome>;
}) {
  const [editing, setEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  function startEditing() {
    // Tier B review fix: `!editable` alone let a click anywhere else in the
    // row — the description text, which bubbles up through `FieldRow`'s own
    // wrapping `onClick={onActivate}` — reopen the `<select>` while a save
    // for this exact toggle was still in flight. That never desynced the
    // displayed value (`resting` above always renders straight from `value`,
    // the query's own cache), but it could race two overlapping `onSave`
    // calls: if the second (from the reopened editor) resolved before the
    // first, the first's own failure branch could then report an error
    // naming a value that was already stale by the time it was shown.
    // Withholding the whole row while `isSaving` is true — the same
    // "disabled" condition the resting button already had — closes it at
    // the one place a save can actually start from, not just on the button.
    if (!editable || isSaving) {
      return;
    }
    setError(undefined);
    setEditing(true);
  }

  async function handleChange(nextRawValue: string) {
    setEditing(false);
    const next = nextRawValue === "true";
    if (next === value) {
      return;
    }
    setIsSaving(true);
    setError(undefined);
    const outcome = await onSave(toggle.key, next);
    setIsSaving(false);
    if (!outcome.ok) {
      // VC-15.11-04: no local state held the new value while saving — `value`
      // is still exactly what the server had before this attempt, so there is
      // nothing to revert beyond simply not having changed it. The error
      // names that plainly rather than leaving the toggle's own state to
      // imply it.
      setError(
        `${outcome.message ?? "Couldn't save."} "${toggle.label}" is still set to "${formatNotificationYesNo(value)}".`,
      );
    }
  }

  const resting = isSaving ? "Saving…" : formatNotificationYesNo(value);

  return (
    <FieldRow label={toggle.label} onActivate={startEditing}>
      {editing ? (
        <select
          autoFocus
          className="h-tap-target-min w-full rounded-sm border border-border-focus bg-surface px-2 text-body text-ink"
          defaultValue={value ? "true" : "false"}
          onChange={(event) => void handleChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              setEditing(false);
            }
          }}
          onClick={(event) => event.stopPropagation()}
          onBlur={(event) => event.stopPropagation()}
        >
          {NOTIFICATION_YES_NO_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <button
          type="button"
          onClick={startEditing}
          disabled={!editable || isSaving}
          className="block w-full cursor-default border-0 bg-transparent p-0 text-left font-[inherit] text-[inherit]"
        >
          {resting}
        </button>
      )}
      <p className="mt-1 text-meta text-ink-secondary">{toggle.description}</p>
      {error ? (
        <p role="alert" className="mt-1 text-meta text-danger">
          {error}
        </p>
      ) : null}
    </FieldRow>
  );
}

/**
 * Story 15.11's own distinct block (spec judgement call 2: "its own labeled
 * block, not folded into either existing group, since these are behavior
 * toggles, not data fields about the person"). Wires up
 * `GET`/`PATCH /users/{user_id}/notifications` (`app/api/users.py`), both
 * `role=Role.OWNER` — a route this frontend has never called before this
 * story (Intent).
 *
 * `enabled: isOwner` (Intent: "this section must not even attempt to fetch
 * the data for a non-Owner viewer, since the backend would refuse the GET
 * itself") is the real gate — VC-15.11-02's network half. This component
 * additionally never renders anything for a non-Owner (`isOwner === false`
 * returns `null` below), which is VC-15.11-02's visibility half — the same
 * "withhold by absence, not by disabling" rule `useIsReadOnly`'s own doc
 * comment already established for this codebase, applied here to `useIsOwner`.
 */
function NotificationsSection({ userId, isOwner }: { userId: string; isOwner: boolean }) {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ["users", "record", userId, "notifications"] as const, [userId]);

  const query = useQuery({
    queryKey,
    queryFn: () => apiFetch<NotificationPreferences>(`/users/${userId}/notifications`),
    enabled: isOwner,
  });

  async function saveToggle(key: keyof NotificationPreferences, next: boolean): Promise<EditCommitOutcome> {
    try {
      const updated = await apiFetch<NotificationPreferences>(`/users/${userId}/notifications`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        // VC-15.11-03: exactly the one field that changed, never the other
        // three re-sent alongside it.
        body: JSON.stringify({ [key]: next }),
      });
      queryClient.setQueryData<NotificationPreferences>(queryKey, updated);
      return { ok: true };
    } catch (error) {
      return {
        ok: false,
        message: error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      };
    }
  }

  if (!isOwner) {
    return null;
  }

  return (
    <section className="mt-section-gap border-t border-border-hairline pt-section-gap">
      <h2 className="text-heading font-semibold text-ink">Notifications</h2>
      {query.isPending ? (
        <p className="mt-2 text-body text-ink-secondary">Loading notification settings…</p>
      ) : query.isError ? (
        <p role="alert" className="mt-2 text-body text-danger">
          {query.error instanceof ApiError
            ? query.error.message
            : "Couldn't load notification settings. Please try again in a moment."}
        </p>
      ) : (
        <div className="divide-y divide-border-hairline">
          {NOTIFICATION_TOGGLES.map((toggle) => (
            <NotificationToggleRow
              key={toggle.key}
              toggle={toggle}
              value={query.data[toggle.key]}
              editable={isOwner}
              onSave={saveToggle}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * `/settings/users/:id` — the real record page (Story 2.5), replacing
 * Story 2.2's `UserRecordPlaceholder`. Fetches its own record directly
 * through the new `GET /users/{id}` route rather than the placeholder's
 * documented workaround (fetching the whole list and finding one row
 * client-side). All five of `UserResource`'s fields fit without hiding any
 * behind "More details" — stated honestly rather than manufacturing a
 * reason to hide one (VC-2.5-12's live half). Provenance markers and
 * inline related records (spec judgement calls 1, 2) have no real
 * `UserResource` analog today, so this route configures neither — both are
 * proven at the component level instead (`RecordPage.test.tsx`,
 * `FieldRow.test.tsx`), honestly, rather than faking a shape here that
 * doesn't exist.
 */
export function UserRecord() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const enumsQuery = useEnums();
  const queryClient = useQueryClient();
  const isReadOnly = useIsReadOnly();
  //: `GET`/`PATCH /users/{user_id}/notifications` are both `role=Role.OWNER`
  //: (`app/api/users.py`) — a Member is not Read-only, so `useIsReadOnly()`
  //: alone would wrongly show/fetch this section for one (spec Intent,
  //: mirroring `PropertyRecord.tsx`'s/`TenantRecord.tsx`'s identical
  //: Owner-only rule for their own `PATCH`-gated fields).
  const isOwner = useIsOwner();
  const latestUpdatedAtRef = useRef<Record<string, string>>({});
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | undefined>(undefined);
  /** Story 2.6's phone-reachable equivalent of the table row's ghost
   * "Remove" action (DESIGN.md: "on phone they live on the record page,
   * never behind a swipe") — also available on desktop, so the same
   * action isn't reachable only from one posture. */
  const [removeOpen, setRemoveOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | undefined>(undefined);
  /** A stable fallback focus target for the confirm dialog, the same
   * reasoning as `UsersList.tsx`'s own `headingRef` — `RecordPage.tsx`
   * owns the actual `<h1>` internally, so this page's own outer wrapper
   * (present the whole time this route is mounted) stands in instead. */
  const contentRef = useRef<HTMLDivElement>(null);

  const queryKey = useMemo(() => ["users", "record", id] as const, [id]);

  const recordQuery = useQuery({
    queryKey,
    queryFn: () => apiFetch<UserResource>(`/users/${id}`),
    enabled: id !== undefined,
  });

  useEffect(() => {
    document.title = recordQuery.data ? `${recordQuery.data.display_name} — PropDesk` : "Record — PropDesk";
  }, [recordQuery.data]);

  function writeRecordToCache(updated: UserResource) {
    latestUpdatedAtRef.current[updated.id] = updated.updated_at;
    queryClient.setQueryData<UserResource>(queryKey, updated);
  }

  async function sendPatch(
    row: UserResource,
    field: DataTableColumn<UserResource>,
    payload: EditCommitPayload,
    expectedUpdatedAt: string,
  ): Promise<EditCommitOutcome> {
    try {
      const updated = await apiFetch<UserResource>(`/users/${row.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...patchBodyFor(field.key, payload),
          expected_updated_at: expectedUpdatedAt,
        }),
      });
      writeRecordToCache(updated);
      return { ok: true };
    } catch (error) {
      if (error instanceof ApiError && error.code === "conflict") {
        const message = conflictMessage(
          {
            field: error.field,
            currentValue: error.currentValue,
            attemptedValue: payload.kind === "value" ? payload.value : undefined,
            fallbackMessage: error.message,
          },
          labelForConflictValue,
        );
        return {
          ok: false,
          conflict: true,
          message,
          ...(error.currentValue !== undefined ? { currentValue: error.currentValue } : {}),
        };
      }
      if (error instanceof ApiError) {
        return { ok: false, message: error.message };
      }
      throw error;
    }
  }

  async function commitEdit(args: InlineEditCommitArgs<UserResource>): Promise<EditCommitOutcome> {
    const expectedUpdatedAt = freshestKnownValue(latestUpdatedAtRef.current, args.row.id, args.row.updated_at);
    return sendPatch(args.row, args.column, args.payload, expectedUpdatedAt);
  }

  /** "Keep mine"/"Keep theirs" (judgement call 4's inline banner), the
   * identical shape `UsersList.tsx`'s own `resolveConflictEdit` uses — a
   * genuine re-fetch of this record, never a re-read of the cache that
   * caused the conflict. */
  async function resolveConflictEdit(args: InlineEditConflictArgs<UserResource>): Promise<EditCommitOutcome> {
    const fresh = await queryClient.fetchQuery({
      queryKey,
      queryFn: () => apiFetch<UserResource>(`/users/${id}`),
    });
    latestUpdatedAtRef.current[fresh.id] = fresh.updated_at;
    if (args.resolution === "theirs") {
      return { ok: true };
    }
    return sendPatch(fresh, args.column, { kind: "value", value: args.myValue }, fresh.updated_at);
  }

  /**
   * Restore (spec judgement call 5): a direct primary action, no
   * confirmation dialog — recovery, not a destructive action.
   *
   * Story 2.6's chosen live toast demonstration (judgement call 5): the
   * page's own fields already change on success (the deletion banner and
   * Restore button disappear, the header updates), but restoring someone's
   * access is exactly "a confirmation of something the user cannot see
   * happen on screen" in the sense DESIGN.md means it — the *account*
   * becoming usable again is an event on the server, not a visible
   * transformation of this page's layout the way, say, a new saved-view
   * chip appearing is. The toast states the fact plainly, never a
   * congratulation.
   */
  async function handleRestore() {
    if (id === undefined) {
      return;
    }
    setRestoring(true);
    setRestoreError(undefined);
    try {
      const restored = await apiFetch<UserResource>(`/users/${id}/restore`, { method: "POST" });
      queryClient.setQueryData<UserResource>(queryKey, restored);
      showToast(`${restored.display_name}'s account is active again.`);
    } catch (error) {
      setRestoreError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setRestoring(false);
    }
  }

  /**
   * Removing a user from their own record page (Story 2.6) — the same
   * already-audited `DELETE /users/{id}` `UsersList.tsx`'s row action
   * calls, reached from the one place a phone visitor can actually reach
   * it. The record itself is gone on success, so this navigates back to
   * the list rather than trying to keep showing a row that no longer
   * exists.
   */
  async function confirmRemove() {
    if (id === undefined) {
      return;
    }
    setIsRemoving(true);
    setRemoveError(undefined);
    try {
      await apiFetch<{ removed: boolean }>(`/users/${id}`, { method: "DELETE" });
      // The record itself is gone on success — the list (without this row)
      // is confirmation enough, the same way the table's own row action
      // needs no toast either (DESIGN.md reserves toasts for something the
      // user *cannot* see happen; leaving this page for a list that no
      // longer shows this row is something they do see happen).
      navigate("/settings/users");
    } catch (error) {
      setRemoveError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsRemoving(false);
    }
  }

  const fields = useMemo<RecordField<UserResource>[]>(() => {
    const roleOptions = enumsQuery.data?.roles.map((role) => ({ value: role, label: ROLE_LABELS[role] })) ?? [];
    const definitions: RecordField<UserResource>[] = [
      { key: "display_name", edit: { kind: "text" } },
      { key: "role", render: (row) => ROLE_LABELS[row.role] },
      { key: "status", render: (row) => <StatusBadge status={row.status} /> },
      { key: "email" },
      { key: "created_at", render: (row) => formatDateCell(row.created_at) },
    ];
    // The fixed-option inline edit only attaches once the real option set
    // has loaded from `GET /meta/enums` (AD-2) — the same rule
    // `UsersList.tsx`'s own `role` column follows.
    if (roleOptions.length > 0) {
      definitions[1] = {
        key: "role",
        render: (row) => ROLE_LABELS[row.role],
        edit: { kind: "select", options: roleOptions },
      };
    }
    return definitions;
  }, [enumsQuery.data]);

  const status: "loading" | "not-found" | "error" | "ready" = recordQuery.isPending
    ? "loading"
    : recordQuery.isError
      ? recordQuery.error instanceof ApiError && recordQuery.error.status === 404
        ? "not-found"
        : "error"
      : "ready";

  const errorMessage = recordQuery.error instanceof ApiError ? recordQuery.error.message : undefined;
  const deletion = recordQuery.data?.deletion
    ? { deletedAt: recordQuery.data.deletion.deleted_at, deletedByDisplayName: recordQuery.data.deletion.deleted_by }
    : undefined;

  return (
    <div ref={contentRef} tabIndex={-1} className="max-w-record-column-max">
      <ReadOnlyNotice className="mb-4" />
      {restoreError ? (
        <p role="alert" className="mb-4 text-meta text-danger">
          {restoreError}
        </p>
      ) : null}
      <RecordPage<UserResource>
        fields={fields}
        row={recordQuery.data}
        status={status}
        {...(errorMessage ? { errorMessage } : {})}
        identifier={(row) => row.display_name}
        backLink={{ label: "Users", href: "/settings/users" }}
        icon={TenantsIcon}
        tint="violet"
        statusBadge={(row) => <StatusBadge status={row.status} />}
        {...(deletion ? { deletion } : {})}
        {...(deletion
          ? {
              primaryAction: {
                label: restoring ? "Restoring…" : "Restore",
                onClick: () => void handleRestore(),
                disabled: restoring,
              },
            }
          : {})}
        {...(!deletion && !isReadOnly && recordQuery.data
          ? {
              secondaryActions: [
                {
                  label: "Remove",
                  ariaLabel: `Remove ${recordQuery.data.display_name}`,
                  onClick: () => setRemoveOpen(true),
                },
              ],
            }
          : {})}
        onCommitEdit={commitEdit}
        onResolveConflict={resolveConflictEdit}
      />
      {/*
        Story 15.11: a distinct section (spec judgement call 2), rendered
        only once this is a real, loaded record — never beneath a
        "not found"/error `RecordPage` state, and never keyed off the raw
        route param (`recordQuery.data.id`, the confirmed resource's own id).
        `NotificationsSection` itself decides whether it renders anything at
        all (`isOwner`) and whether its own query even fires — this call
        site does not duplicate either gate.
      */}
      {recordQuery.data ? <NotificationsSection userId={recordQuery.data.id} isOwner={isOwner} /> : null}
      {recordQuery.data ? (
        <ConfirmDialog
          open={removeOpen}
          onOpenChange={(open) => {
            setRemoveOpen(open);
            if (!open) {
              setRemoveError(undefined);
            }
          }}
          title="Remove this person?"
          description={`Removing ${recordQuery.data.display_name} takes away their access right away and ends any sessions they're signed in to. Their account can be restored for 30 days.`}
          confirmLabel={`Remove ${recordQuery.data.display_name}`}
          confirmingLabel="Removing…"
          isConfirming={isRemoving}
          onConfirm={() => void confirmRemove()}
          fallbackFocusRef={contentRef}
          {...(removeError ? { errorMessage: removeError } : {})}
        />
      ) : null}
    </div>
  );
}
